import { AppleIDAuthResponse } from '@/components/AppleAuth/types/appleAuth';
import { AppleReauth } from '@/components/AppleAuth/AppleReauth';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidderEmail } from '@/redux/modules/account/user/user.selectors';
import { getDeskSupportLink } from '@/utils/urls';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { openResendConfirmationEmailModal } from '@/redux/modules/modal';
import {
    resetAccountDeleteSlice,
    submitDeleteAccount,
} from '@/redux/modules/account/deleteAccount/deleteAccount.actions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useState } from 'react';
import StatusMessage from './StatusMessage';

type DeleteAccountConfirmationProps = {
    bidderId: number;
    token: string;
};

type DeleteAccountStatus = 'loading' | 'success' | 'expired' | 'error' | 'appleReauthError';

const LOADING_ANIMATION_SIZE = 50;

const SupportLink = (
    <Link
        href={getDeskSupportLink()}
        target="_blank"
    >
        <FormattedMessage id="deleteAccountConfirmation.support" />
    </Link>
);

const DeleteAccountConfirmation = ({ bidderId, token }: DeleteAccountConfirmationProps) => {
    const dispatch = useAppDispatch();
    const userEmail = useAppSelector(getBidderEmail);
    const [status, setStatus] = useState<DeleteAccountStatus>('loading');

    // If delete account request fails, user can resend the confirmation email
    // This will open the resend confirmation modal and resend the request to send the confirmation email
    const openResendConfirmationModal = useCallback(() => {
        dispatch(resetAccountDeleteSlice());
        dispatch(openResendConfirmationEmailModal());
    }, [dispatch]);

    const onSubmitDeleteAccount = useCallback(
        (appleReauthResponse?: AppleIDAuthResponse) => {
            setStatus('loading');
            dispatch(submitDeleteAccount({ appleCode: appleReauthResponse?.code, bidderId, token }))
                .unwrap()
                .then(() => {
                    setStatus('success');
                })
                .catch((error: { message?: string }) => {
                    console.error({ error });
                    if (error?.message?.includes('Invalid Parameters - Link has expired')) {
                        setStatus('expired');
                    } else if (error?.message === 'Unable to close account, please sign in with Apple and try again') {
                        setStatus('appleReauthError');
                    } else {
                        setStatus('error');
                    }
                });
        },
        [bidderId, dispatch, token]
    );

    // Immediately submit the delete account request once the page is loaded
    useEffect(() => {
        onSubmitDeleteAccount();
    }, [bidderId, token, dispatch, onSubmitDeleteAccount]);

    // Show correct status message based on response
    switch (status) {
        case 'loading':
            return <Loading size={LOADING_ANIMATION_SIZE} />;
        case 'success':
            return (
                <StatusMessage
                    showHomePageLink
                    subtitleId="deleteAccountConfirmation.success.subtitle"
                    subtitleValues={{
                        link: SupportLink,
                    }}
                    titleId="deleteAccountConfirmation.success.title"
                />
            );

        case 'expired':
            return (
                <StatusMessage
                    email={userEmail}
                    onCtaClick={openResendConfirmationModal}
                    showHomePageLink={false}
                    subtitleId="deleteAccountConfirmation.expired.subtitle"
                    titleId="deleteAccountConfirmation.expired.title"
                />
            );

        case 'error':
            return (
                <StatusMessage
                    email={userEmail}
                    onCtaClick={openResendConfirmationModal}
                    showHomePageLink={false}
                    subtitleId="deleteAccountConfirmation.error.subtitle"
                    subtitleValues={{
                        link: SupportLink,
                    }}
                    titleId="deleteAccountConfirmation.error.title"
                />
            );
        case 'appleReauthError':
            return (
                <div>
                    <StatusMessage
                        email={userEmail}
                        isAppleReauthError
                        onCtaClick={openResendConfirmationModal}
                        showHomePageLink={false}
                        subtitleId="deleteAccountConfirmation.appleReauthError.subtitle"
                        titleId="deleteAccountConfirmation.appleReauthError.title"
                    />
                    <AppleReauth
                        className="w-1/2 xsMax:w-full"
                        onSuccess={onSubmitDeleteAccount}
                    />
                </div>
            );

        default:
            return null;
    }
};

export default DeleteAccountConfirmation;
